import { countryOptions, type ApiAddress, type EnumCountry } from '@Model';
import {
    EditFrameMode,
    GridColumns,
    Select,
    TextInput,
    useTranslate,
    type IGuiBinder,
} from '@synqo/frontend-component-library';
import { AddressCityColumn } from './AddressCityColumn';
import { AddressPostalCodeInput } from './AddressPostalCodeInput';
import { useLocalityAutomation } from './useLocalityAutomation';

// @ai-generated 2026-09-21T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5

export interface AddressLocalityFieldsProps {
    apiAddress: ApiAddress;
    binder: IGuiBinder<ApiAddress>;
    /** Persists a city the postal code automation determined. */
    applyCity: (city: string) => void;
    /**
     * The mode the frame is in. Leaving a write mode for `View` is the save — or the discard, which
     * amounts to the same thing here — and the automation lets go of its own city there.
     */
    mode: EditFrameMode;
}

/**
 * The locality row of an address: postal code, city, district and country. It wires the two fields
 * that belong together — the postal code drives the locality lookup, and everything that answer
 * means is said below the city field, which is the field it is about.
 *
 * Only a writable city field gets the automation: where nothing may be written there is nothing to
 * fill in, nothing to decide and nothing to say, so the city is an ordinary field and no lookup is
 * made at all. The binder is the authority on that, in view mode as well as for a field the server
 * marked read-only.
 */
export function AddressLocalityFields({ apiAddress, binder, applyCity, mode }: AddressLocalityFieldsProps) {
    const i18n = useTranslate('');

    const cityProps = binder.bindToProp<string>('city');
    const isCityWritable = cityProps.locked !== true;

    const automation = useLocalityAutomation({
        apiAddress,
        applyCity,
        enabled: isCityWritable,
        isEditing: mode !== EditFrameMode.View,
    });

    return (
        <GridColumns spans={[3, 6, 6, 3]}>
            <AddressPostalCodeInput
                binder={binder}
                country={apiAddress.country}
                onZipTyped={automation.onZipTyped}
                inputRef={automation.zipInputRef}
            />
            {isCityWritable ? (
                <AddressCityColumn
                    binder={binder}
                    automation={automation}
                    hint={mode === EditFrameMode.Create ? i18n('Wird aus der PLZ vorgeschlagen') : undefined}
                />
            ) : (
                <TextInput {...cityProps} label={i18n('Stadt')} />
            )}
            <TextInput {...binder.bindToProp<string>('district')} label={i18n('Ortszusatz')} />
            <Select<EnumCountry>
                items={countryOptions}
                {...binder.bindToAutoConfirmed('country')}
                label={i18n('Land')}
                searchable
            />
        </GridColumns>
    );
}
